import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/api/axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { Plus, Upload, FileText, AlertCircle, Loader2 } from 'lucide-react';

const AddPolicyPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    insurer_name: '',
    policy_type: '',
    policy_number: '',
    start_date: '',
    expiry_date: '',
    premium: '',
    notes: ''
  });

  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const policyTypes = [
    { value: 'Health', label: 'Health Insurance' },
    { value: 'Car', label: 'Car Insurance' },
    { value: 'Bike', label: 'Bike Insurance' },
    { value: 'Life', label: 'Life Insurance' },
    { value: 'Other', label: 'Other' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Check file size (10MB)
      if (selectedFile.size > 10 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, file: 'File size must be less than 10MB' }));
        return;
      }
      setFile(selectedFile);
      setErrors(prev => ({ ...prev, file: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.insurer_name.trim()) {
      newErrors.insurer_name = 'Insurer name is required';
    }
    
    if (!formData.policy_type) {
      newErrors.policy_type = 'Policy type is required';
    }
    
    if (!formData.policy_number.trim()) {
      newErrors.policy_number = 'Policy number is required';
    }
    
    if (!formData.start_date) {
      newErrors.start_date = 'Start date is required';
    }
    
    if (!formData.expiry_date) {
      newErrors.expiry_date = 'Expiry date is required';
    }
    
    // Validate expiry > start
    if (formData.start_date && formData.expiry_date) {
      if (new Date(formData.expiry_date) <= new Date(formData.start_date)) {
        newErrors.expiry_date = 'Expiry date must be after start date';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }
    
    setSubmitting(true);
    
    try {
      let documentUrl = null;
      
      // Upload file if present
      if (file) {
        setUploading(true);
        const formData = new FormData();
        formData.append('file', file);
        
        const uploadResponse = await api.post('/upload-document', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        documentUrl = uploadResponse.data.file_url;
        setUploading(false);
      }
      
      // Create policy
      const policyData = {
        insurer_name: formData.insurer_name,
        policy_type: formData.policy_type,
        policy_number: formData.policy_number,
        start_date: new Date(formData.start_date).toISOString(),
        expiry_date: new Date(formData.expiry_date).toISOString(),
        premium: formData.premium ? parseFloat(formData.premium) : null,
        document_url: documentUrl,
        notes: formData.notes || null
      };
      
      await api.post('/user-policies', policyData);
      
      toast.success('Policy added successfully!');
      navigate('/my-policies');
    } catch (error) {
      console.error('Failed to add policy:', error);
      toast.error(error.response?.data?.detail || 'Failed to add policy');
    } finally {
      setSubmitting(false);
      setUploading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-secondary-50 flex items-center justify-center">
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-secondary-600 mb-4">Please login to add policies</p>
            <Button onClick={() => navigate('/login')}>Login</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary-50" data-testid="add-policy-page">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl py-8">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                <Plus className="h-6 w-6 text-primary-600" />
              </div>
              <div>
                <CardTitle className="text-2xl font-bold font-heading">Add Your Policy</CardTitle>
                <CardDescription>Enter details of your existing insurance policy</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Insurer Name */}
              <div className="space-y-2">
                <Label htmlFor="insurer_name" className="text-sm font-semibold">
                  Insurance Company Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="insurer_name"
                  name="insurer_name"
                  value={formData.insurer_name}
                  onChange={handleInputChange}
                  placeholder="e.g., HDFC ERGO, ICICI Lombard"
                  data-testid="insurer-name-input"
                />
                {errors.insurer_name && (
                  <p className="text-sm text-red-600">{errors.insurer_name}</p>
                )}
              </div>

              {/* Policy Type */}
              <div className="space-y-2">
                <Label htmlFor="policy_type" className="text-sm font-semibold">
                  Policy Type <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.policy_type}
                  onValueChange={(value) => {
                    setFormData(prev => ({ ...prev, policy_type: value }));
                    if (errors.policy_type) {
                      setErrors(prev => ({ ...prev, policy_type: null }));
                    }
                  }}
                >
                  <SelectTrigger data-testid="policy-type-select">
                    <SelectValue placeholder="Select policy type" />
                  </SelectTrigger>
                  <SelectContent>
                    {policyTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.policy_type && (
                  <p className="text-sm text-red-600">{errors.policy_type}</p>
                )}
              </div>

              {/* Policy Number */}
              <div className="space-y-2">
                <Label htmlFor="policy_number" className="text-sm font-semibold">
                  Policy Number <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="policy_number"
                  name="policy_number"
                  value={formData.policy_number}
                  onChange={handleInputChange}
                  placeholder="Enter your policy number"
                  data-testid="policy-number-input"
                />
                {errors.policy_number && (
                  <p className="text-sm text-red-600">{errors.policy_number}</p>
                )}
              </div>

              {/* Dates */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start_date" className="text-sm font-semibold">
                    Start Date <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    type="date"
                    id="start_date"
                    name="start_date"
                    value={formData.start_date}
                    onChange={handleInputChange}
                    data-testid="start-date-input"
                  />
                  {errors.start_date && (
                    <p className="text-sm text-red-600">{errors.start_date}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="expiry_date" className="text-sm font-semibold">
                    Expiry Date <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    type="date"
                    id="expiry_date"
                    name="expiry_date"
                    value={formData.expiry_date}
                    onChange={handleInputChange}
                    data-testid="expiry-date-input"
                  />
                  {errors.expiry_date && (
                    <p className="text-sm text-red-600">{errors.expiry_date}</p>
                  )}
                </div>
              </div>

              {/* Premium */}
              <div className="space-y-2">
                <Label htmlFor="premium" className="text-sm font-semibold">
                  Annual Premium (Optional)
                </Label>
                <Input
                  type="number"
                  id="premium"
                  name="premium"
                  value={formData.premium}
                  onChange={handleInputChange}
                  placeholder="Enter annual premium amount"
                  data-testid="premium-input"
                />
              </div>

              {/* Document Upload */}
              <div className="space-y-2">
                <Label htmlFor="document" className="text-sm font-semibold">
                  Policy Document (Optional)
                </Label>
                <div className="flex items-center gap-4">
                  <Input
                    type="file"
                    id="document"
                    onChange={handleFileChange}
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="flex-1"
                    data-testid="document-input"
                  />
                  {file && (
                    <div className="flex items-center gap-2 text-sm text-secondary-600">
                      <FileText className="h-4 w-4" />
                      <span>{file.name}</span>
                    </div>
                  )}
                </div>
                <p className="text-xs text-secondary-500">
                  Supported formats: PDF, JPG, PNG. Max size: 10MB
                </p>
                {errors.file && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{errors.file}</AlertDescription>
                  </Alert>
                )}
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes" className="text-sm font-semibold">
                  Additional Notes (Optional)
                </Label>
                <Textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Any additional information about this policy"
                  rows={3}
                  data-testid="notes-input"
                />
              </div>

              {/* Submit */}
              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/my-policies')}
                  className="flex-1"
                  data-testid="cancel-button"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={submitting || uploading}
                  data-testid="submit-button"
                >
                  {uploading ? (
                    <>
                      <Upload className="mr-2 h-4 w-4 animate-pulse" />
                      Uploading...
                    </>
                  ) : submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Adding Policy...
                    </>
                  ) : (
                    <>
                      <Plus className="mr-2 h-4 w-4" />
                      Add Policy
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AddPolicyPage;
